import { createContext, useContext, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import useQueryParams from '../hooks/useQueryParams';
import { toggleDocumentAttribute } from '../utils/layout';

const ThemeContext = createContext(undefined);

/** Same provider as `useLayoutContext`; use with `useContext` when a missing-provider fallback is needed. */
export const LayoutThemeContext = ThemeContext;

export const useLayoutContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useLayoutContext must be used within LayoutProvider');
  return context;
};

/** Default layout theme when nothing is stored yet (ignore OS prefers-color-scheme). */
const getPreferredTheme = () => 'light';

export const LayoutProvider = ({ children }) => {
  const queryParams = useQueryParams();
  const override = !!(queryParams.layout_theme || queryParams.topbar_theme || queryParams.menu_theme || queryParams.menu_size);
  const INIT_STATE = {
    theme: queryParams.layout_theme || getPreferredTheme(),
    topbarTheme: queryParams.topbar_theme || 'light',
    menu: {
      theme: queryParams.menu_theme || 'light',
      size: queryParams.menu_size || 'sm-hover-active',
    },
  };

  const [settings, setSettings] = useLocalStorage('__SANDUK_ADMIN_CONFIG__', INIT_STATE, override);

  // Never persist 'hidden' — restore to expanded on refresh
  if (settings.menu?.size === 'hidden') {
    settings.menu.size = 'sm-hover-active';
  }
  const [offcanvasStates, setOffcanvasStates] = useState({
    showThemeCustomizer: false,
    showActivityStream: false,
    showBackdrop: false,
  });

  const updateSettings = (newSettings) => setSettings((prev) => ({ ...prev, ...newSettings }));
  const changeTheme = (theme) => updateSettings({ theme });
  const changeTopbarTheme = (topbarTheme) => updateSettings({ topbarTheme });
  const changeMenuTheme = (theme) => updateSettings({ menu: { ...settings.menu, theme } });
  const changeMenuSize = (size) => updateSettings({ menu: { ...settings.menu, size } });

  const toggleThemeCustomizer = () =>
    setOffcanvasStates((s) => ({ ...s, showThemeCustomizer: !s.showThemeCustomizer }));
  const toggleActivityStream = () =>
    setOffcanvasStates((s) => ({ ...s, showActivityStream: !s.showActivityStream }));

  const toggleBackdrop = useCallback(() => {
    const htmlTag = document.documentElement;
    if (offcanvasStates.showBackdrop) htmlTag.classList.remove('sidebar-enable');
    else htmlTag.classList.add('sidebar-enable');
    setOffcanvasStates((s) => ({ ...s, showBackdrop: !s.showBackdrop }));
  }, [offcanvasStates.showBackdrop]);

  // Apply before paint so Bootstrap tokens + consumers (e.g. logos) match stored theme immediately.
  useLayoutEffect(() => {
    const layoutTheme =
      String(settings?.theme ?? 'light').toLowerCase() === 'dark' ? 'dark' : 'light';
    toggleDocumentAttribute('data-bs-theme', layoutTheme);
    toggleDocumentAttribute('data-topbar-color', settings.topbarTheme);
    toggleDocumentAttribute('data-menu-color', settings.menu.theme);
    toggleDocumentAttribute('data-menu-size', settings.menu.size);
    return () => {
      toggleDocumentAttribute('data-bs-theme', layoutTheme, true);
      toggleDocumentAttribute('data-topbar-color', settings.topbarTheme, true);
      toggleDocumentAttribute('data-menu-color', settings.menu.theme, true);
      toggleDocumentAttribute('data-menu-size', settings.menu.size, true);
    };
  }, [settings]);

  const value = useMemo(() => {
    const normalizedTheme =
      String(settings?.theme ?? 'light').toLowerCase() === 'dark' ? 'dark' : 'light';
    return {
      ...settings,
      theme: normalizedTheme,
      themeMode: normalizedTheme,
      changeTheme,
      changeTopbarTheme,
      changeMenu: { theme: changeMenuTheme, size: changeMenuSize },
      themeCustomizer: { open: offcanvasStates.showThemeCustomizer, toggle: toggleThemeCustomizer },
      activityStream: { open: offcanvasStates.showActivityStream, toggle: toggleActivityStream },
      toggleBackdrop,
      resetSettings: () => updateSettings(INIT_STATE),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: stable setters, INIT_STATE is constant
  }, [settings, offcanvasStates]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
      {offcanvasStates.showBackdrop && (
        <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop} aria-hidden />
      )}
    </ThemeContext.Provider>
  );
};
